// Shared zod primitives used by the per-slice schemas.
import { z } from 'zod';

// UUIDv4 / v7-friendly opaque id pattern. We don't lock to v4 because Drizzle
// might use ULID/UUIDv7-style identifiers; we only require the canonical 36-char
// dashed format.
export const idSchema = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'invalid id format');

export const emailSchema = z
  .string()
  .min(3)
  .max(254)
  .email()
  .transform((v) => v.toLowerCase());

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters.')
  .max(200, 'That password is unusually long — keep it under 200 characters.');

export const slugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'lowercase, numbers, hyphens — no leading or trailing hyphen');

/**
 * Vocab slug — accepts the same character set as `slugSchema` PLUS underscores.
 * Used for controlled-vocab references (art form, venue type, vibe tag,
 * amenity, country) where the seed-time data uses snake_case identifiers
 * like `solo_musician`. Existence + enabled-status checks happen at the
 * worker route boundary via `services/vocab.findMissingSlugs`.
 */
export const vocabSlugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(
    /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/,
    'lowercase letters, digits, hyphens, or underscores — no leading or trailing punctuation',
  );

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'use YYYY-MM-DD');

export const isoTimestampSchema = z
  .string()
  .datetime({ offset: true });

export const localeSchema = z.string().min(2).max(10).default('en-AU');
export const timezoneSchema = z.string().min(1).max(40).default('Australia/Brisbane');

export const currencyCodeSchema = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'ISO 4217 three-letter code');

export const countryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2 (uppercase)');

export const latSchema = z.number().gte(-90).lte(90);
export const lngSchema = z.number().gte(-180).lte(180);

export const httpsUrlSchema = z
  .string()
  .url()
  .refine((u) => u.startsWith('https://'), { message: 'must use https://' });

/**
 * An image reference we will render in an <img src>. Accepts either an absolute
 * https:// URL (an artist linking an off-platform photo) or a same-origin path
 * we minted ourselves.
 *
 * Uploads land in R2 via `uploadPublic()`, which returns a ROOT-RELATIVE path
 * (`/img/img/<hash>.jpg`) rather than an absolute URL — it has no way to know the
 * request's origin. Validating those with `httpsUrlSchema` is a contract break:
 * the upload route writes a value that the profile-edit route then refuses to
 * accept back, so saving a profile after uploading a photo fails. Hence this
 * schema, which is the correct validator for any field an upload can populate.
 *
 * The leading-slash form is deliberately narrow — a bare `/…` path, never a
 * protocol-relative `//host/…` (which is an absolute URL to another origin) and
 * never a `/\` variant that some parsers also treat as protocol-relative.
 */
export const imageRefSchema = z
  .string()
  .refine(
    (u) =>
      (u.startsWith('/') && !u.startsWith('//') && !u.startsWith('/\\')) ||
      (/^https:\/\//.test(u) && URL.canParse(u)),
    { message: 'must be an https:// URL or an uploaded image path' },
  );

export const moneyMinorSchema = z.number().int().nonnegative();

// Free-text validators. Strip C0/C1 control chars but keep tab and newline.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
export const richTextSchema = (max: number) =>
  z
    .string()
    .max(max)
    .transform((v) => v.replace(CONTROL_CHARS, ''));

// Display-name / venue-name / artist-name validator. NFC-normalise so
// visually-identical strings compare equal, then strip:
//   - zero-width chars (U+200B-200F, U+FEFF) — homograph hiding
//   - bidi overrides (U+202A-202E, U+2066-2069) — RTL override attacks
//   - C0/C1 control chars (defence-in-depth; richText also strips these)
// Then collapse runs of whitespace and trim, since copy-pasted display names
// often arrive with stray spacing. The min/max are caller-supplied because
// display_name (80 chars) and venue.name (120 chars) have different limits.
// Explicit \u escapes for readability + ASCII-only source.
// U+200B–U+200F (ZWSP / ZWJ / LRM / RLM), U+202A–U+202E (LRE/RLE/PDF/LRO/RLO),
// U+2066–U+2069 (LRI/RLI/FSI/PDI), U+FEFF (BOM / ZWNBSP).
const INVISIBLE_CHARS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;
export const safeNameSchema = (min: number, max: number) =>
  z
    .string()
    .min(min)
    .max(max)
    .transform((v) => {
      const normalised = v
        .normalize('NFC')
        .replace(INVISIBLE_CHARS, '')
        .replace(CONTROL_CHARS, '')
        .replace(/\s+/g, ' ')
        .trim();
      return normalised;
    })
    .refine((v) => v.length >= min, {
      message: `Must be at least ${min} characters after normalisation.`,
    });

export const csrfTokenSchema = z.string().min(16).max(256);

// Cursor envelope for paginated reads.
export const paginationQuerySchema = z.object({
  cursor: z.string().min(1).max(256).nullish(),
  limit: z.number().int().positive().max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
